import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  InputAdornment,
  TablePagination,
  useTheme,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Autocomplete,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Snackbar,
  Alert,
  Typography,
  Paper,
  Divider
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useSelector } from 'react-redux';
import {
  fetchMembers,
  addMember,
  updateMember,
  deleteMember,
  fetchCenterNames
} from '../../../Services/FirebaseUtils';

const MemberList = () => {
  const [members, setMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [centers, setCenters] = useState([]);
  const [selectedBaseCenter, setSelectedBaseCenter] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [actionMember, setActionMember] = useState(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [viewingMember, setViewingMember] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingCenters, setLoadingCenters] = useState(false);
  const [savingMember, setSavingMember] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const theme = useTheme();
  const user = useSelector((state) => state.user.data);

  const [memberForm, setMemberForm] = useState({
    companyName: '',
    startDate: '',
    endDate: '',
    baseCenter: '',
    status: 'active',
    creditsPerMonth: '',
    referralCode: '',
    emailDomains: []
  });

  const [emailDomain, setEmailDomain] = useState('');

  const handleAddDomain = () => {
    if (emailDomain && !memberForm.emailDomains.includes(emailDomain)) {
      setMemberForm({
        ...memberForm,
        emailDomains: [...memberForm.emailDomains, emailDomain]
      });
      setEmailDomain('');
    }
  };

  const handleRemoveDomain = (domainToRemove) => {
    setMemberForm({
      ...memberForm,
      emailDomains: memberForm.emailDomains.filter(domain => domain !== domainToRemove)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSavingMember(true);

    try {
      const memberData = {
        ...memberForm,
        operatorId: user.operatorId,
        createdAt: new Date().toISOString()
      };

      if (editingMember) {
        const result = await updateMember(editingMember.id, memberData);
        if (result.success) {
          setSnackbar({ open: true, message: 'Member updated successfully', severity: 'success' });
          loadMembers();
          setIsEditDialogOpen(false);
        } else {
          setSnackbar({ open: true, message: result.error || 'Failed to update member', severity: 'error' });
        }
      } else {
        const result = await addMember(memberData);
        if (result.success) {
          setSnackbar({ open: true, message: 'Member added successfully', severity: 'success' });
          loadMembers();
          setIsAddDialogOpen(false);
        } else {
          setSnackbar({ open: true, message: result.error || 'Failed to add member', severity: 'error' });
        }
      }
    } catch (error) {
      console.error('Error saving member:', error);
      setSnackbar({ open: true, message: 'Failed to save member', severity: 'error' });
    } finally {
      setSavingMember(false);
    }
  };

  useEffect(() => {
    loadMembers();
    loadCenters();
  }, [user]);

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const loadMembers = async () => {
    if (user?.operatorId) {
      setLoading(true);
      try {
        const result = await fetchMembers(user.operatorId);
        if (result.success) {
          setMembers(result.members);
        } else {
          showSnackbar(result.error || 'Failed to load members', 'error');
        }
      } catch (error) {
        console.error('Error loading members:', error);
        showSnackbar('Error loading members', 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  const loadCenters = async () => {
    if (user?.operatorId) {
      setLoadingCenters(true);
      try {
        const result = await fetchCenterNames(user.operatorId);
        if (result.success) {
          setCenters(result.centers);
        } else {
          showSnackbar(result.error || 'Failed to load centers', 'error');
        }
      } catch (error) {
        console.error('Error loading centers:', error);
        showSnackbar('Error loading centers', 'error');
      } finally {
        setLoadingCenters(false);
      }
    }
  };

  const getCenterName = (centerId) => {
    const center = centers.find(c => c.id === centerId);
    return center ? center.centerName : '-';
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleActionsClick = (event, member) => {
    setAnchorEl(event.currentTarget);
    setActionMember(member);
  };

  const handleActionsClose = () => {
    setAnchorEl(null);
    setActionMember(null);
  };

  const handleAddMember = () => {
    setMemberForm({
      companyName: '',
      startDate: '',
      endDate: '',
      baseCenter: '',
      status: 'active',
      creditsPerMonth: '',
      referralCode: '',
      emailDomains: []  // Initialize as empty array
    });
    setIsAddDialogOpen(true);
  };

  const handleEditMember = (member) => {
    setEditingMember(member);
    setMemberForm({
      companyName: member.companyName,
      startDate: member.startDate,
      endDate: member.endDate,
      baseCenter: member.baseCenter || '',
      status: member.status,
      creditsPerMonth: member.creditsPerMonth,
      referralCode: member.referralCode,
      emailDomains: member.emailDomains || []  // Use existing domains or empty array
    });
    setIsEditDialogOpen(true);
    handleActionsClose();
  };

  const handleViewMember = (member) => {
    setViewingMember(member);
    setIsViewDialogOpen(true);
    handleActionsClose();
  };

  const handleDeleteMember = async (memberId) => {
    try {
      const result = await deleteMember(memberId);
      if (result.success) {
        await loadMembers();
      } else {
        showSnackbar(result.error || 'Failed to delete member', 'error');
      }
    } catch (error) {
      console.error('Error deleting member:', error);
      showSnackbar('Error deleting member', 'error');
    }
    handleActionsClose();
  };

  const handleCloseViewDialog = () => {
    setIsViewDialogOpen(false);
    setViewingMember(null);
  };

  const filteredMembers = members.filter(member =>
    (member.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.baseCenter.toLowerCase().includes(searchTerm.toLowerCase())) &&
    (!selectedBaseCenter || member.baseCenter === selectedBaseCenter)
  );

  const paginatedMembers = filteredMembers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const renderMemberDialog = () => (
    <Dialog
      open={isAddDialogOpen || isEditDialogOpen}
      onClose={isAddDialogOpen ? () => setIsAddDialogOpen(false) : () => setIsEditDialogOpen(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {isEditDialogOpen ? 'Edit Member' : 'Add New Member'}
      </DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          <Stack spacing={3}>
            <TextField
              fullWidth
              label="Company Name"
              value={memberForm.companyName}
              onChange={(e) => setMemberForm({ ...memberForm, companyName: e.target.value })}
              required
            />



            <TextField
              fullWidth
              label="Credits Per Month"
              type="number"
              value={memberForm.creditsPerMonth}
              onChange={(e) => setMemberForm({ ...memberForm, creditsPerMonth: e.target.value })}
              required
            />

            <FormControl fullWidth>
              <InputLabel>Base Center</InputLabel>
              <Select
                value={memberForm.baseCenter}
                onChange={(e) => setMemberForm({ ...memberForm, baseCenter: e.target.value })}
                label="Base Center"
                required
              >
                {centers.map((center) => (
                  <MenuItem key={center.id} value={center.id}>
                    {center.centerName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={memberForm.startDate}
              onChange={(e) => setMemberForm({ ...memberForm, startDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              required
            />

            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={memberForm.endDate}
              onChange={(e) => setMemberForm({ ...memberForm, endDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              required
            />

            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={memberForm.status}
                onChange={(e) => setMemberForm({ ...memberForm, status: e.target.value })}
                label="Status"
                required
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Referral Code"
              value={memberForm.referralCode}
              onChange={(e) => setMemberForm({ ...memberForm, referralCode: e.target.value })}
            />
          </Stack>
        </Box>

        <Box>
          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Email Domain"
              value={emailDomain}
              onChange={(e) => setEmailDomain(e.target.value)}
              placeholder="example.com"
              helperText="Enter domain without @ symbol"
            />
            <Button
              sx={{ 
                mt: 2,
                minWidth: '60px',
                height: '40px',
                padding: '0 8px'
              }}
              variant="contained"
              onClick={handleAddDomain}
              disabled={!emailDomain}
              size="small"
            >
              Add
            </Button>
          </Stack>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
            {(memberForm.emailDomains || []).map((domain, index) => (
              <Chip
                key={index}
                label={domain}
                onDelete={() => handleRemoveDomain(domain)}
              />
            ))}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={isAddDialogOpen ? () => setIsAddDialogOpen(false) : () => setIsEditDialogOpen(false)}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={savingMember}
        >
          {savingMember ? (
            <CircularProgress size={24} />
          ) : (
            isEditDialogOpen ? 'Update' : 'Add'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderViewDialog = () => (
    <Dialog
      open={isViewDialogOpen}
      onClose={handleCloseViewDialog}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ 
        borderBottom: '1px solid #e0e0e0',
        pb: 2
      }}>
        Member Details
      </DialogTitle>
      <DialogContent>
        {viewingMember && (
          <Box sx={{ mt: 2 }}>
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Company Name
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {viewingMember.companyName}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Email Domains
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {(viewingMember.emailDomains || []).map((domain, index) => (
                    <Chip
                      key={index}
                      label={domain}
                      size="small"
                      sx={{ 
                        bgcolor: 'primary.light',
                        color: 'primary.dark'
                      }}
                    />
                  ))}
                  {(!viewingMember.emailDomains || viewingMember.emailDomains.length === 0) && (
                    <Typography variant="body2" color="text.secondary">
                      No email domains added
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Credits Per Month
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {viewingMember.creditsPerMonth || '0'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Base Center
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {getCenterName(viewingMember?.baseCenter)}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Duration
                </Typography>
                <Stack direction="row" spacing={4}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Start Date
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {viewingMember.startDate || '-'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      End Date
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {viewingMember.endDate || '-'}
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Status
                </Typography>
                <Chip
                  label={viewingMember.status}
                  color={viewingMember.status === 'active' ? 'success' : 'error'}
                  size="small"
                  sx={{ 
                    fontWeight: 500,
                    textTransform: 'capitalize'
                  }}
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Referral Code
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {viewingMember.referralCode || '-'}
                </Typography>
              </Box>
            </Stack>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid #e0e0e0', p: 2 }}>
        <Button 
          onClick={handleCloseViewDialog}
          variant="contained"
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );

  const menuItems = [
    {
      icon: <VisibilityIcon />,
      text: 'View Details',
      onClick: () => handleViewMember(actionMember)
    },
    {
      icon: <EditIcon />,
      text: 'Edit',
      onClick: () => handleEditMember(actionMember)
    },
    {
      icon: <DeleteIcon />,
      text: 'Delete',
      onClick: () => handleDeleteMember(actionMember)
    }
  ];

  return (
    <Box sx={{ p: 0 }}>
      <Card>
        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <TextField
              variant="outlined"
              placeholder="Search members..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                minWidth: 300,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  '& fieldset': {
                    borderColor: '1px solid #f5f5f5',
                  },
                  '&:hover fieldset': {
                    borderColor: '1px solid #f5f5f5',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '1px solid #f5f5f5',
                  },
                },
                '& .MuiInputBase-input': {
                  padding: '8px 14px',
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddMember}
            >
              Add Member
            </Button>
          </Box>

          <Box sx={{
            height: "2px",
            backgroundColor: "#e0e0e0",
            width: "calc(100% + 42px)",
            mx: -3,
            mb: 2
          }} />

          {/* Table */}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: (theme) => `${theme.palette.primary.main}08` }}>
                  <TableCell
                    sx={{
                      fontWeight: 500,
                      color: "#6B7280",
                      borderBottom: "1px solid #E5E7EB",
                      py: 1.5
                    }}>Company Name</TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 500,
                      color: "#6B7280",
                      borderBottom: "1px solid #E5E7EB",
                      py: 1.5
                    }}>Start Date</TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 500,
                      color: "#6B7280",
                      borderBottom: "1px solid #E5E7EB",
                      py: 1.5
                    }}>End Date</TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 500,
                      color: "#6B7280",
                      borderBottom: "1px solid #E5E7EB",
                      py: 1.5
                    }}>Base Center</TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 500,
                      color: "#6B7280",
                      borderBottom: "1px solid #E5E7EB",
                      py: 1.5
                    }}>Status</TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 500,
                      color: "#6B7280",
                      borderBottom: "1px solid #E5E7EB",
                      py: 1.5
                    }}>Credits/Month</TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 500,
                      color: "#6B7280",
                      borderBottom: "1px solid #E5E7EB",
                      py: 1.5
                    }}>Referral Code</TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 500,
                      color: "#6B7280",
                      borderBottom: "1px solid #E5E7EB",
                      py: 1.5
                    }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : paginatedMembers.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell>
                      {member.companyName}
                    </TableCell>
                    <TableCell>{member.startDate}</TableCell>
                    <TableCell>{member.endDate}</TableCell>
                    <TableCell>{getCenterName(member.baseCenter)}</TableCell>
                    <TableCell>
                      <Chip
                        label={member.status}
                        color={member.status === 'active' ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{member.creditsPerMonth}</TableCell>
                    <TableCell>{member.referralCode}</TableCell>
                    <TableCell align="right">
                      <IconButton onClick={(e) => handleActionsClick(e, member)}>
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredMembers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      </Card>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleActionsClose}
      >
        {menuItems.map((item, index) => (
          <MenuItem key={index} onClick={item.onClick}>
            <ListItemIcon>
              {item.icon}
            </ListItemIcon>
            <ListItemText>{item.text}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      {renderMemberDialog()}
      {renderViewDialog()}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MemberList;
